import { FC } from 'react'
import { Alert, Button, Card, Empty, List, Space, Tag, Typography, message } from 'antd'
import { CalendarOutlined, CheckOutlined, ClockCircleOutlined, TeamOutlined } from '@ant-design/icons'
import { useRegisterTrainingRound } from 'hooks/useTraining'
import { isEmailRequiredError } from 'services/eventCalendarApi'
import { TrainingInvitation } from 'services/interface/eventCalendar/eventCalendar.interface'
import { thaiDate } from 'utils/dateFormat'
import { seatLabel, timeRange } from 'utils/trainingFormat'

const { Text, Title } = Typography

type InvitationRound = TrainingInvitation['rounds'][number]

/**
 * คำเชิญเข้าอบรมที่ยังรอตอบ — ของเดิมมีอยู่แต่ในอีเมลฉบับเดียว
 *
 * พลาดเมลนั้น (เข้าถังขยะ / กรอกอีเมลผิด / ลบทิ้ง) แล้วผู้ประกอบการไม่มีทางกลับเข้า
 * กระบวนการเองเลย ต้องรอทีมงานโทรตาม การ์ดนี้ยกคำเชิญฉบับเดียวกันมาไว้ในเมนูอบรม
 *
 * สองโหมด ตามที่ทีมงานเลือกตอนส่งคำเชิญ:
 *   นัดรอบไว้ให้แล้ว → แถวเดียว: วันเวลา + ปุ่ม "ยืนยันเข้าร่วม"
 *   ให้เลือกวันเอง   → รอบเป็นรายการ กดปุ่มในแถวเดียวจบ
 *
 * **ทั้งสองปุ่มยิง `register/:event_id` เส้นเดียวกับการจองปกติ** ไม่มีตรรกะจองชุดที่สอง —
 * จึงได้การกันที่นั่งเกิน อีเมลยืนยัน และไฟล์ปฏิทิน เหมือนกันทุกทาง และแถวที่เขียนเป็น
 * `ref_type='factory'` ซึ่งฝั่ง Lead CRM นับว่า "ตอบคำเชิญแล้ว" ผ่าน frf.factory_id
 *
 * ตอบสำเร็จแล้วไม่ต้องซ่อนเอง: backend หยุดส่งคำเชิญใบนั้นมาเมื่อ**มีการจองจริง**
 * (ไม่มี flag "ตอบแล้ว" ให้หลุด sync) — hook invalidate คีย์ invitations ให้แล้ว
 */
const TrainingInvitationCard: FC<{
  invitation: TrainingInvitation
  /** ด่าน UX ก่อนยิง — ด่านจริงอยู่ฝั่ง server (400 EMAIL_REQUIRED) */
  ensureEmail: () => boolean
  onNeedEmail: () => void
  /** เปิดหน้ารายละเอียดหลักสูตร (drawer ตัวเดียวกับแท็บ "อบรมที่เปิดรับ") */
  onOpenDetail: (eventId: string) => void
}> = ({ invitation, ensureEmail, onNeedEmail, onOpenDetail }) => {
  const registerMutation = useRegisterTrainingRound()

  const fixedRound = invitation.rounds.find(round => round.id === invitation.fixed_round_id)
  const isOnline = (invitation.training_type || 'Online') !== 'Onsite'

  const confirm = async (round: InvitationRound) => {
    if (!ensureEmail()) return

    try {
      await registerMutation.mutateAsync({ event_id: invitation.event_id, round_id: round.id })
      message.success(`ยืนยันเข้าอบรมรอบ ${thaiDate(round.round_date)} เรียบร้อยแล้ว`)
    } catch (error: any) {
      if (isEmailRequiredError(error)) {
        onNeedEmail()

        return
      }
      // เหตุผลจาก backend ตรง ๆ (รอบเต็มระหว่างกด / คำเชิญถูกถอน) — ผู้ใช้ต้องรู้ว่า
      // ทำไมกดแล้วไม่เกิดอะไร ไม่ใช่เงียบหาย
      message.error(
        error?.response?.data?.description ||
          error?.response?.data?.message ||
          'ยืนยันการอบรมไม่สำเร็จ กรุณาลองใหม่'
      )
    }
  }

  return (
    <Card
      size="small"
      style={{ borderColor: '#91caff', background: '#f0f7ff' }}
      title={
        <Space size={4} wrap>
          <Title level={5} style={{ margin: 0 }}>
            {invitation.event_name ?? 'หลักสูตรอบรม'}
          </Title>
        </Space>
      }
      extra={
        // ป้ายบอกว่า "ต้องทำอะไรต่อ" — สำคัญเมื่อมีคำเชิญค้างพร้อมกันหลายใบ
        // ใช้คำของผู้ประกอบการ ไม่ใช่ invitation_type_label ซึ่งเป็นศัพท์ฝั่งแอดมิน
        <Tag color="blue">{fixedRound ? 'กำหนดรอบแล้ว' : 'เลือกวันเอง'}</Tag>
      }
      actions={[
        // การ์ดตั้งใจให้ตัดสินใจจบในตัว (วัน/เวลา/ที่นั่ง) — เนื้อหาหลักสูตรฉบับเต็ม
        // อยู่ใน drawer เดิม ไม่ทำหน้ารายละเอียดชุดที่สองให้ต้องแก้สองที่
        <Button type="link" key="detail" onClick={() => onOpenDetail(invitation.event_id)}>
          ดูรายละเอียดหลักสูตร
        </Button>,
      ]}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        {isOnline ? 'อบรมออนไลน์' : 'อบรมที่สถานที่จัด'}
        {invitation.instructor ? ` · วิทยากร ${invitation.instructor}` : ''}
      </Text>

      {/* ── นัดรอบไว้ให้แล้ว: ไม่มีอะไรให้เลือก เหลือแค่ยืนยัน ── */}
      {fixedRound && (
        <>
          <Text style={{ display: 'block', marginBottom: 8 }}>
            ทีมงานกำหนดรอบอบรมให้ท่านแล้ว กรุณายืนยันเข้าร่วม
          </Text>
          <Card size="small" bodyStyle={{ padding: 12 }}>
            <Space
              style={{ width: '100%', justifyContent: 'space-between' }}
              size={[8, 8]}
              wrap
            >
              <Space direction="vertical" size={0}>
                <Text strong>
                  <CalendarOutlined /> {thaiDate(fixedRound.round_date)}
                </Text>
                <Text type="secondary">
                  <ClockCircleOutlined /> {timeRange(fixedRound.start_time, fixedRound.end_time)}
                  {/* title มักมีคำว่า "รอบ" อยู่แล้ว ("รอบที่ 2") — เติมอีกได้ "รอบรอบที่ 2" */}
                  {fixedRound.title ? ` · ${fixedRound.title}` : ''}
                </Text>
              </Space>
              <Button
                type="primary"
                loading={registerMutation.isPending}
                onClick={() => confirm(fixedRound)}
              >
                ยืนยันเข้าร่วม
              </Button>
            </Space>
          </Card>
        </>
      )}

      {/* ── ให้เลือกวันเอง: รอบเป็นรายการ กดปุ่มในแถวเดียวจบ ── */}
      {!fixedRound &&
        (invitation.rounds.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="รอบอบรมของหลักสูตรนี้ยังไม่เปิดรับ ทีมงานจะแจ้งเมื่อมีรอบใหม่"
          />
        ) : (
          <List
            size="small"
            header={<Text type="secondary">เลือกวันที่ท่านสะดวก</Text>}
            dataSource={invitation.rounds}
            renderItem={round => (
              <List.Item
                actions={[
                  round.is_full ? (
                    <Tag color="red" key="full">
                      เต็ม
                    </Tag>
                  ) : (
                    <Button
                      key="pick"
                      type="primary"
                      size="small"
                      icon={<CheckOutlined />}
                      loading={
                        registerMutation.isPending &&
                        registerMutation.variables?.round_id === round.id
                      }
                      disabled={registerMutation.isPending}
                      onClick={() => confirm(round)}
                    >
                      เลือกรอบนี้
                    </Button>
                  ),
                ]}
              >
                <List.Item.Meta
                  title={thaiDate(round.round_date)}
                  description={
                    <Space size={8} wrap>
                      <Text type="secondary">{timeRange(round.start_time, round.end_time)}</Text>
                      {round.title && <Text type="secondary">{round.title}</Text>}
                      <Text type={round.is_full ? 'danger' : 'success'}>
                        <TeamOutlined /> {seatLabel(round)}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ))}

      {!isOnline && (
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          showIcon
          message="อบรมที่สถานที่จัด — รายละเอียดสถานที่อยู่ในอีเมลยืนยันหลังจองสำเร็จ"
        />
      )}
    </Card>
  )
}

export default TrainingInvitationCard
